import { useMemo } from "react";
import type { Course } from "../../../stores/course.store";
import type { CourseSubscription, Member } from "../../../stores/user.store";
import type { TemporarySubscription } from "../../../types/course";

export type SubscriptionMode = "create-new" | "edit-temporary" | "view-paid";

export const useSubscriptionMode = (
  member: Member | undefined,
  currentCourse: Course | null,
  tempSubscriptions: Map<string, TemporarySubscription>
): {
  mode: SubscriptionMode;
  dbSubscription: CourseSubscription | null;
  tempSubscription: TemporarySubscription | null;
  isReadOnly: boolean;
} => {
  // Check for existing DB subscription (paid)
  const dbSubscription = useMemo(() => {
    if (!member || !currentCourse) {
      return null;
    }
    return member.course_subscriptions?.find(
      (sub) => sub.course_id === currentCourse.id
    ) ?? null;
  }, [member, currentCourse]);

  // Check for temporary subscription
  const tempSubscription = useMemo(() => {
    if (!member?.id) {
      return null;
    }
    return tempSubscriptions.get(member.id) || null;
  }, [tempSubscriptions, member?.id]);

  // Determine mode
  const mode = useMemo((): SubscriptionMode => {
    if (dbSubscription) {
      return "view-paid";
    }
    if (tempSubscription) {
      return "edit-temporary";
    }
    return "create-new";
  }, [dbSubscription, tempSubscription]);

  const isReadOnly = mode === "view-paid";

  return {
    mode,
    dbSubscription,
    tempSubscription,
    isReadOnly,
  };
};
